import { filter, find } from 'es-toolkit/compat';
import FavoritePageUnit from 'models/listingPage/FavoritePageUnit';
import RenterAccountBookmark from 'models/renterAccounts/RenterAccountBookmark';
import RenterAccountLease from 'models/renterAccounts/RenterAccountLease';
import RenterLeaseDetail from 'models/renterAccounts/RenterLeaseDetail';
import { createSelector } from 'reselect';
import { LeaseStatus } from '../../../models/enums';
import { AppState } from '../types';
import { RENTAL_ACCOUNT_REDUCER } from './reducer';
import { isTourRequestActive } from './utils';

const _selectRentalAccountReducer = (state: AppState) =>
  state[RENTAL_ACCOUNT_REDUCER];

const _selectLeases = (state: AppState) =>
  _selectRentalAccountReducer(state).leases;
const _selectBookmarks = (state: AppState) =>
  _selectRentalAccountReducer(state).bookmarks;

const _selectAccountUnits = (state: AppState) =>
  _selectRentalAccountReducer(state).units;
const _selectAccountUnitById = (state: AppState, unitId: string) =>
  _selectAccountUnits(state)[unitId];
const _selectPayments = (state: AppState) =>
  _selectRentalAccountReducer(state).payments;
const _selectFavoriteHomes = (state: AppState) =>
  _selectRentalAccountReducer(state).favoriteHomes;

const _selectFavoriteHomesAccounts = (state: AppState) =>
  _selectRentalAccountReducer(state).favoriteAccountHomes;

export const selectResidentOccupiedTours = (state: AppState) =>
  _selectRentalAccountReducer(state).occupiedTours;

// export const selectResidentCurrentTask = createSelector(
//   _selectResidentCurrentTask,
//   task => new MaintenanceTaskModel(task)
// );

export const selectAccountUnitById = createSelector(
  _selectAccountUnitById,
  (unit) => new RenterLeaseDetail(unit),
);

const _selectAccountLeaseById = (state: AppState, leaseId: string) => {
  const leases = _selectLeases(state);
  return find(
    leases,
    (lease: any) => lease.leaseInfo.basicInfo.leaseId === leaseId,
  );
};

// The lease slice is populated by untyped JS actions, so every entry below is
// `any` for the same reason `AppState` is.
export const selectAccountAllLeases = createSelector(
  _selectLeases,
  (leases) => {
    const leaseModels = leases
      .filter(
        (l: any) =>
          l?.leaseInfo?.basicInfo?.status !== LeaseStatus.Offered &&
          l?.leaseInfo?.basicInfo?.status !== LeaseStatus.ResidentsSigned,
      )
      .map((lease: any) => new RenterAccountLease(lease));
    const newestLeaseForUnits = leaseModels.reduce((acc: any, lease: any) => {
      const { unitId } = lease.unitInfo;
      const existingLease = acc[unitId];
      if (
        !existingLease ||
        existingLease.leaseInfo.basicInfo.leaseEndOn <
          lease.leaseInfo.basicInfo.leaseEndOn
      ) {
        acc[unitId] = lease;
      }
      return acc;
    }, {});
    return Object.values(newestLeaseForUnits);
  },
);

export const selectLeases = createSelector(_selectLeases, (leases) => {
  return leases.map((lease: any) => new RenterAccountLease(lease));
});

export const selectOfferedAndSignedLeases = createSelector(
  _selectLeases,
  (leases) => {
    if (!leases) {
      return null;
    }

    const offeredAndSignedLeases = filter(
      leases,
      (lease: any) =>
        lease?.leaseInfo?.basicInfo?.status !== LeaseStatus.Offered ||
        lease?.leaseInfo?.basicInfo?.status !== LeaseStatus.ResidentsSigned,
    );
    return offeredAndSignedLeases.map(
      (lease: any) => new RenterAccountLease(lease),
    );
  },
);

export const selectBookmarks = createSelector(_selectBookmarks, (bookmarks) =>
  bookmarks
    ? bookmarks.map((bookmark: any) => new RenterAccountBookmark(bookmark))
    : null,
);

export const selectTourBookmarks = createSelector(
  _selectBookmarks,
  (bookmarks) =>
    bookmarks
      ? bookmarks
          .filter((bookmark: any) => !!bookmark.tourType)
          .map((bookmark: any) => new RenterAccountBookmark(bookmark))
      : null,
);

export const selectActiveTourRequests = createSelector(
  (state: AppState) => _selectRentalAccountReducer(state),
  (reducerState) =>
    reducerState
      ? reducerState.tourRequests?.filter(isTourRequestActive)
      : null,
);

export const selectListingApplicationBookmarks = createSelector(
  _selectBookmarks,
  (bookmarks) =>
    bookmarks
      ? bookmarks
          .filter((bookmark: any) => !!bookmark.applicationId)
          .map((bookmark: any) => new RenterAccountBookmark(bookmark))
      : null,
);

export const selectAccountLeaseById = createSelector(
  _selectAccountLeaseById,
  (lease) => (lease ? new RenterAccountLease(lease) : null),
);

const _selectTasks = (state: AppState) =>
  _selectRentalAccountReducer(state).tasks;

export const selectFavoriteHomes = createSelector(
  _selectFavoriteHomes,
  (favoriteHomes) => new FavoritePageUnit(favoriteHomes).favoriteHomes,
);

export const selectFavoriteHomesAccounts = createSelector(
  _selectFavoriteHomesAccounts,
  (favoriteHomes) => new FavoritePageUnit(favoriteHomes).favoriteHomes,
);

export const selectResidentFlows = (state: AppState) =>
  _selectRentalAccountReducer(state).flows;

export const selectLeaseSignatureInformation = createSelector(
  _selectLeases,
  (leases) => {
    return leases.map((lease: any) => ({
      leaseId: lease.leaseInfo.basicInfo.leaseId,
      signatureInfo: lease.leaseInfo.basicInfo.signatureInfo,
      isSelfServe: lease.leaseInfo.basicInfo.isSelfServe,
    }));
  },
);

export const selectListingApplicationDetails = createSelector(
  [_selectRentalAccountReducer],
  (renterAccounts) => renterAccounts.listingApplicationDetails || {},
);

export const selectUserProfiles = createSelector(
  [_selectRentalAccountReducer],
  (renterAccounts) => renterAccounts.userProfiles || {},
);

const selectLeaseAdvisors = createSelector(
  [_selectRentalAccountReducer],
  (renterAccounts) => renterAccounts.leaseAdvisors || {},
);

export const selectLeaseAdvisorByKey = createSelector(
  [selectLeaseAdvisors, (state, key) => key],
  (leaseAdvisors, key) => leaseAdvisors[key],
);
